import { useState, useMemo, useEffect } from 'react'
import {
  useProductCategories,
  useCreateProductCategory,
  useUpdateProductCategory,
  useDeleteProductCategory,
} from '@/admin/hooks/use-products'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/shared/components/ui/dialog'
import { Button } from '@/shared/components/ui/button'
import { Plus, Pencil, Trash2 } from 'lucide-react'
import { toast } from 'sonner'
import { useTranslation } from 'react-i18next'
import type { ProductCategory } from '@/admin/types/products'
import { cn } from '@/shared/lib/utils'
import { CategoryIcon, DEFAULT_ICON_KEY } from '@/admin/components/category-icons'
import { LucideIconPicker } from '@/shared/components/ui/lucide-icon-picker'
import { useForm } from '@/shared/lib/forms'
import type { FormField } from '@/shared/lib/forms'
import { ConfirmDeleteDialog } from '@/admin/components/confirm-delete-dialog'

import { toastApiError } from '@/shared/lib/api-error'
export function ProductCategoriesPage() {
  const { t } = useTranslation()
  const { data: categories, isLoading } = useProductCategories()
  const [showAddDialog, setShowAddDialog] = useState(false)
  const [editingCategory, setEditingCategory] = useState<ProductCategory | null>(null)

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <span className="clara-section-label">{t('categories.title')}</span>
        <Button
          data-testid="categories-add-btn"
          onClick={() => setShowAddDialog(true)}
          className="flex items-center gap-1.5 px-3 py-1.5 rounded-[4px] text-[11px] uppercase tracking-[0.1em] transition-all duration-150 cursor-pointer bg-[var(--green)] text-[var(--bg)] hover:bg-[var(--green-hover)]"
        >
          <Plus className="h-3.5 w-3.5" />
          {t('categories.addCategory')}
        </Button>
      </div>
      {isLoading ? (
        <p className="text-[13px] text-[var(--ink-3)]">{t('common.loading')}</p>
      ) : !categories?.length ? (
        <p className="text-[13px] text-[var(--ink-3)]">{t('categories.emptyState')}</p>
      ) : (
        <div className="overflow-hidden rounded-[8px] border-[0.5px] border-[var(--rule)]">
          {categories.map((cat, idx) => (
            <CategoryNode
              key={cat.uuid}
              category={cat}
              depth={0}
              isLast={idx === categories.length - 1}
              onEdit={setEditingCategory}
            />
          ))}
        </div>
      )}
      {/* Add dialog */}
      <CategoryDialog
        open={showAddDialog}
        onOpenChange={setShowAddDialog}
        allCategories={categories ?? []}
      />
      {/* Edit dialog */}
      {editingCategory && (
        <CategoryDialog
          open={true}
          onOpenChange={(open) => {
            if (!open) setEditingCategory(null)
          }}
          allCategories={categories ?? []}
          existingCategory={editingCategory}
        />
      )}
    </div>
  );
}

function CategoryNode({
  category,
  depth,
  isLast,
  onEdit,
}: {
  category: ProductCategory
  depth: number
  isLast: boolean
  onEdit: (cat: ProductCategory) => void
}) {
  const { t } = useTranslation()
  const deleteCategory = useDeleteProductCategory()
  const [deleteOpen, setDeleteOpen] = useState(false)

  async function handleDelete() {
    try {
      await deleteCategory.mutateAsync(category.uuid)
      toast.success(t('categories.toasts.deleted'))
      setDeleteOpen(false)
    } catch (err) {
      toastApiError(err, t('categories.toasts.deleteFailed'));
    }
  }

  return (
    <>
      <div
        data-testid={`categories-item-${category.uuid}`}
        className={cn(
          'group flex items-center gap-3 px-4 py-3 transition-all duration-100 hover:bg-[var(--bg-subtle)]',
          !(isLast && (!category.children || category.children.length === 0)) &&
            'border-b-[0.5px] border-b-[var(--rule)]',
        )}
      >
        {Array.from({ length: depth }).map((_, idx) => (
          <div key={`${category.uuid}-indent-${idx}`} className="w-6 shrink-0" aria-hidden="true" />
        ))}
        <CategoryIcon icon={category.icon || ''} className="h-4 w-4 shrink-0 text-[var(--ink-3)]" />
        <div className="flex-1 min-w-0">
          <span className="block text-[13px] text-[var(--ink)]">
            {category.name}
          </span>
          {category.description && (
            <span className="text-[11px] text-[var(--ink-3)]">
              {category.description}
            </span>
          )}
        </div>
        <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity duration-150">
          <Button
            variant="ghost"
            size="icon-xs"
            data-testid={`categories-edit-${category.uuid}`}
            onClick={() => onEdit(category)}
            className="h-6 w-6 cursor-pointer text-[var(--ink-3)] hover:bg-[var(--bg-subtle)]"
          >
            <Pencil className="h-3 w-3" />
          </Button>
          <Button
            variant="ghost"
            size="icon-xs"
            data-testid={`categories-delete-${category.uuid}`}
            onClick={() => setDeleteOpen(true)}
            className="h-6 w-6 cursor-pointer text-[var(--ink-3)] hover:bg-[var(--bg-subtle)]"
          >
            <Trash2 className="h-3 w-3" />
          </Button>
        </div>
      </div>
      {category.children?.map((child, idx) => (
        <CategoryNode
          key={child.uuid}
          category={child}
          depth={depth + 1}
          isLast={idx === category.children.length - 1}
          onEdit={onEdit}
        />
      ))}
      <ConfirmDeleteDialog
        open={deleteOpen}
        onOpenChange={setDeleteOpen}
        title={`Delete ${category.name}?`}
        description={
          <>
            Delete <strong>{category.name}</strong>? This action cannot be undone.
          </>
        }
        onConfirm={handleDelete}
        isPending={deleteCategory.isPending}
      />
    </>
  );
}

function flattenCategories(cats: ProductCategory[], depth = 0): { cat: ProductCategory; depth: number }[] {
  const result: { cat: ProductCategory; depth: number }[] = []
  for (const cat of cats) {
    result.push({ cat, depth })
    if (cat.children?.length) {
      result.push(...flattenCategories(cat.children, depth + 1))
    }
  }
  return result
}

function CategoryDialog({
  open,
  onOpenChange,
  allCategories,
  existingCategory,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  allCategories: ProductCategory[]
  existingCategory?: ProductCategory
}) {
  const { t } = useTranslation()
  const createCategory = useCreateProductCategory()
  const updateCategory = useUpdateProductCategory()

  const isEdit = !!existingCategory
  const flatCats = flattenCategories(allCategories)
  const fields = useMemo<FormField[]>(
    () => [
      {
        type: 'text',
        name: 'name',
        label: t('common.name'),
        placeholder: t('categories.namePlaceholder'),
        value: existingCategory?.name ?? '',
        autoFocus: true,
        required: 'Name is required',
        onChange: (value, formContext) => {
          if (!isEdit) {
            formContext?.setFieldValue(
              'slug',
              String(value ?? '')
                .toLowerCase()
                .replace(/[^a-z0-9]+/g, '-')
                .replace(/^-|-$/g, ''),
            )
          }
        },
      },
      {
        type: 'text',
        name: 'slug',
        label: t('categories.slug'),
        placeholder: 'kitchen-appliances',
        value: existingCategory?.slug ?? '',
        required: 'Slug is required',
      },
      {
        type: 'text',
        name: 'description',
        label: t('common.description'),
        placeholder: t('categories.descriptionPlaceholder'),
        value: existingCategory?.description ?? '',
      },
      {
        type: 'custom',
        name: 'icon',
        value: existingCategory?.icon || DEFAULT_ICON_KEY,
        render: (formContext) => (
          <LucideIconPicker
            value={String(formContext.values.icon ?? DEFAULT_ICON_KEY)}
            onChange={(value) => formContext.setFieldValue('icon', value)}
            mode="floating"
            label="Icon"
          />
        ),
      },
      {
        type: 'select',
        name: 'parent_category_uuid',
        label: t('categories.parentCategory'),
        value: existingCategory?.parent_category_uuid ?? '__none__',
        options: [
          { value: '__none__', label: t('categories.noParent') },
          ...flatCats
            .filter((f) => f.cat.uuid !== existingCategory?.uuid)
            .map((f) => ({
              value: f.cat.uuid,
              label: `${'\u00A0'.repeat(f.depth * 2)}${f.cat.name}`,
            })),
        ],
      },
    ],
    [
      existingCategory?.description,
      existingCategory?.icon,
      existingCategory?.name,
      existingCategory?.parent_category_uuid,
      existingCategory?.slug,
      existingCategory?.uuid,
      flatCats,
      isEdit,
      t,
    ],
  )
  const { Form, ref } = useForm(
    {
      name: `product-category-dialog-${existingCategory?.uuid ?? 'new'}`,
      submit: 'footer',
      fields,
    },
    async (rawValues) => {
      const values = rawValues as Record<string, string>
      const payload = {
        name: values.name.trim(),
        slug: values.slug.trim(),
        description: values.description.trim(),
        icon: values.icon || DEFAULT_ICON_KEY,
        parent_category_uuid:
          values.parent_category_uuid && values.parent_category_uuid !== '__none__'
            ? values.parent_category_uuid
            : null,
      }
      try {
        if (isEdit && existingCategory) {
          await updateCategory.mutateAsync({
            uuid: existingCategory.uuid,
            ...payload,
          })
          toast.success(t('categories.toasts.updated'))
        } else {
          await createCategory.mutateAsync(payload)
          toast.success(t('categories.toasts.created'))
        }
        onOpenChange(false)
      } catch (err) {
        toastApiError(err, isEdit ? t('categories.toasts.updateFailed') : t('categories.toasts.createFailed'));
      }
    },
    [existingCategory?.uuid ?? 'new', open, allCategories.length, fields],
  )

  // Hard-reset on open: the shared form keeps dirty values across reopens and a
  // submit-close skips the on-close reset, so "+ Add" would show stale values.
  useEffect(() => {
    if (!open) return
    ref.current?.resetForm({
      values: {
        name: existingCategory?.name ?? '',
        slug: existingCategory?.slug ?? '',
        description: existingCategory?.description ?? '',
        icon: existingCategory?.icon || DEFAULT_ICON_KEY,
        parent_category_uuid: existingCategory?.parent_category_uuid ?? '__none__',
      },
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, existingCategory?.uuid])

  return (
    <Dialog
      open={open}
      onOpenChange={(nextOpen) => {
        if (!nextOpen) {
          ref.current?.resetForm({
            values: {
              name: existingCategory?.name ?? '',
              slug: existingCategory?.slug ?? '',
              description: existingCategory?.description ?? '',
              icon: existingCategory?.icon || DEFAULT_ICON_KEY,
              parent_category_uuid: existingCategory?.parent_category_uuid ?? '__none__',
            },
          })
        }
        onOpenChange(nextOpen)
      }}
    >
      <DialogContent className="sm:max-w-sm">
        <DialogHeader>
          <DialogTitle>{isEdit ? t('categories.editCategory') : t('categories.addCategory')}</DialogTitle>
        </DialogHeader>
        <Form className="flex flex-col gap-4 py-2" />
        <DialogFooter>
          <Button
            type="submit"
            form={`product-category-dialog-${existingCategory?.uuid ?? 'new'}`}
            data-testid="categories-dialog-submit"
            disabled={createCategory.isPending || updateCategory.isPending}
            className="px-4 py-1.5 rounded-[4px] text-[11px] uppercase tracking-[0.1em] transition-all duration-150 cursor-pointer disabled:opacity-40 bg-[var(--green)] text-[var(--bg)] hover:bg-[var(--green-hover)]"
          >
            {isEdit ? t('common.save') : t('common.create')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
